import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';

import PageContainer from '../components/PageContainer';
import ModePreview from '../modes/ModePreview';
import { domainsApi, ModeSuggestionDetailResponse } from '../api/domains';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import {
    parseModeLimitFromApiError,
    type ModeLimitDetail,
} from '../utils/modeLimitMessaging';

function parseApiErrorMessage(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
        return detail;
    }
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const message = (detail as { message?: string }).message;
        if (message) return message;
        const code = (detail as { code?: string }).code;
        if (code === 'MODE_SUGGESTION_EXPIRED') {
            return 'This mode suggestion has expired.';
        }
        if (code === 'invalid_suggestion_status') {
            return message || 'This suggestion can no longer be approved.';
        }
    }
    return 'Something went wrong. Please try again.';
}

function isNotFoundError(error: unknown): boolean {
    const status = (error as { response?: { status?: number } })?.response?.status;
    return status === 404;
}

function isExpiredError(error: unknown): boolean {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        return (detail as { code?: string }).code === 'MODE_SUGGESTION_EXPIRED';
    }
    return false;
}

const ModeSuggestionReviewPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setActiveDomain, refresh } = useAppBootstrap();

    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState<ModeSuggestionDetailResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [expired, setExpired] = useState(false);
    const [approving, setApproving] = useState(false);
    const [approveError, setApproveError] = useState<string | null>(null);
    const [modeLimitDetail, setModeLimitDetail] = useState<ModeLimitDetail | null>(null);
    const [rejecting, setRejecting] = useState(false);
    const [deferring, setDeferring] = useState(false);
    const [decisionError, setDecisionError] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(
        null,
    );
    const [pendingNavigateTo, setPendingNavigateTo] = useState<string | null>(null);

    const requestIdRef = useRef(0);

    const returnTo = useMemo(() => {
        const raw = searchParams.get('returnTo');
        if (!raw || !raw.startsWith('/app/')) return '/app/profile';
        return raw;
    }, [searchParams]);

    const manualWizardHref = useMemo(() => {
        const params = new URLSearchParams();
        if (returnTo !== '/app/profile') {
            params.set('returnTo', returnTo);
        }
        const query = params.toString();
        return query ? `/app/modes/new?${query}` : '/app/modes/new';
    }, [returnTo]);

    const loadPreview = useCallback(async () => {
        if (!id) {
            setNotFound(true);
            setLoading(false);
            return;
        }
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);
        setNotFound(false);
        setExpired(false);
        try {
            const data = await domainsApi.getModeSuggestion(id);
            if (requestId !== requestIdRef.current) return;
            setPreview(data);
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            if (isExpiredError(err)) {
                setExpired(true);
                setPreview(null);
                return;
            }
            if (isNotFoundError(err)) {
                setNotFound(true);
                setPreview(null);
                return;
            }
            setError(parseApiErrorMessage(err));
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    }, [id]);

    useEffect(() => {
        void loadPreview();
    }, [loadPreview]);

    const handleApprove = async () => {
        if (!id) return;
        setApproving(true);
        setApproveError(null);
        setModeLimitDetail(null);
        setDecisionError(null);
        try {
            const result = await domainsApi.approveModeSuggestion(id);
            try {
                await setActiveDomain(result.mode.key);
            } catch {
                // Mode was created; preference update failure should not block success UX.
            }
            await refresh();
            setToast({ message: `Mode "${result.mode.label}" created.`, severity: 'success' });
            setPendingNavigateTo(returnTo);
        } catch (err) {
            const limitDetail = parseModeLimitFromApiError(err);
            if (limitDetail) {
                setModeLimitDetail(limitDetail);
                return;
            }
            setApproveError(parseApiErrorMessage(err));
        } finally {
            setApproving(false);
        }
    };

    const handleReject = async () => {
        if (!id) return;
        setRejecting(true);
        setDecisionError(null);
        try {
            await domainsApi.rejectModeSuggestion(id);
            setToast({ message: 'Suggestion rejected.', severity: 'success' });
            setPendingNavigateTo(returnTo);
        } catch (err) {
            setDecisionError(parseApiErrorMessage(err));
        } finally {
            setRejecting(false);
        }
    };

    const handleDefer = async () => {
        if (!id) return;
        setDeferring(true);
        setDecisionError(null);
        try {
            await domainsApi.deferModeSuggestion(id);
            setToast({ message: 'You can return to this suggestion later.', severity: 'success' });
            setPendingNavigateTo(returnTo);
        } catch (err) {
            setDecisionError(parseApiErrorMessage(err));
        } finally {
            setDeferring(false);
        }
    };

    return (
        <PageContainer maxWidth="900px">
            <div className="glass-card border-none bg-theme-secondary p-6 md:p-8 shadow-sm">
                <div className="mb-4 flex justify-end">
                    <Link
                        to={returnTo}
                        className="rounded-lg border border-theme px-4 py-2 text-sm text-theme-secondary hover:text-theme hover:bg-theme-card-hover"
                    >
                        Back
                    </Link>
                </div>
                <ModePreview
                    loading={loading}
                    preview={preview}
                    error={error}
                    notFound={notFound}
                    expired={expired}
                    approving={approving}
                    approveError={approveError}
                    modeLimitDetail={modeLimitDetail}
                    rejecting={rejecting}
                    deferring={deferring}
                    decisionError={decisionError}
                    manualWizardHref={manualWizardHref}
                    onRetry={() => void loadPreview()}
                    onApprove={() => void handleApprove()}
                    onReject={() => void handleReject()}
                    onDefer={() => void handleDefer()}
                />
            </div>
            <Snackbar
                open={toast !== null}
                autoHideDuration={4000}
                onClose={() => {
                    setToast(null);
                    if (pendingNavigateTo) {
                        navigate(pendingNavigateTo);
                        setPendingNavigateTo(null);
                    }
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert
                        onClose={() => {
                            setToast(null);
                            if (pendingNavigateTo) {
                                navigate(pendingNavigateTo);
                                setPendingNavigateTo(null);
                            }
                        }}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {toast.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </PageContainer>
    );
};

export default ModeSuggestionReviewPage;
